import type { KeyboardEvent as ReactKeyboardEvent } from "react";
import { type ContextMenuItem, type InteractiveItem, isOpenableSubmenu } from "./items";
import { focusMoveForKey, isTypeaheadKey, nextIndex, typeaheadIndex } from "./navigation";
import type { InitialFocus } from "./useInitialFocus";

/** What a panel exposes to its keyboard handling. */
export interface KeyboardMenu {
  items: ContextMenuItem[];
  /** Positions of the interactive items in `items`. */
  interactive: number[];
  focusedPosition: () => number;
  focusItem: (index: number | undefined) => void;
  activate: (index: number, submenuFocus: InitialFocus) => void;
  openSubmenu: (index: number) => void;
  /** ← : a submenu closes itself; the root ignores it. */
  onBack: () => void;
  /** Escape: a submenu closes itself; the root closes the whole menu. */
  onEscape: () => void;
  closeAndReturnFocus: () => void;
}

/** A key that acts on the menu; `focused` is the index (in `items`) of the focused row. */
type Command = (menu: KeyboardMenu, focused: number | undefined) => void;

const openFocusedSubmenu: Command = (menu, focused) => {
  if (focused !== undefined && isOpenableSubmenu(menu.items[focused])) menu.openSubmenu(focused);
};

const activateFocused: Command = (menu, focused) => {
  if (focused !== undefined) menu.activate(focused, "first-item");
};

const COMMANDS = new Map<string, Command>([
  ["ArrowRight", openFocusedSubmenu],
  ["ArrowLeft", (menu) => menu.onBack()],
  ["Escape", (menu) => menu.onEscape()],
  ["Tab", (menu) => menu.closeAndReturnFocus()],
  ["Enter", activateFocused],
  [" ", activateFocused],
]);

function focusByTypeahead(menu: KeyboardMenu, position: number, char: string): void {
  const labels = menu.interactive.map((index) => (menu.items[index] as InteractiveItem).label);
  const next = typeaheadIndex(labels, position, char);
  if (next >= 0) menu.focusItem(menu.interactive[next]);
}

/** Arrows, Home/End, PageUp/PageDown, → and ← for submenus, Escape, Tab, Enter/Space and typeahead. */
export function handleMenuKey(e: ReactKeyboardEvent<HTMLElement>, menu: KeyboardMenu): void {
  e.stopPropagation();
  const position = menu.focusedPosition();
  const move = focusMoveForKey(e.key);
  if (move) {
    e.preventDefault();
    menu.focusItem(menu.interactive[nextIndex(menu.interactive.length, position, move)]);
    return;
  }
  const command = COMMANDS.get(e.key);
  if (command) {
    e.preventDefault();
    command(menu, position >= 0 ? menu.interactive[position] : undefined);
    return;
  }
  if (isTypeaheadKey(e)) focusByTypeahead(menu, position, e.key);
}
